import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';
import AppointmentScheduleDialog from '../clinic/AppointmentScheduleDialog';
import { formatOutcomeReturnDate } from './evolutionOutcomeModel';

interface EvolutionScheduleReturnPromptProps {
  open: boolean;
  patientId: string;
  patientName?: string;
  professionalId?: string;
  returnDate: string;
  summary: string;
  appointmentType?: 'return' | 'consultation' | 'procedure' | 'exam' | 'emergency' | 'surgery' | 'courtesy';
  onClose: () => void;
  onScheduled?: () => void;
}

const EvolutionScheduleReturnPrompt: React.FC<EvolutionScheduleReturnPromptProps> = ({
  open,
  patientId,
  patientName,
  professionalId,
  returnDate,
  summary,
  appointmentType = 'return',
  onClose,
  onScheduled,
}) => {
  const [scheduleOpen, setScheduleOpen] = useState(false);

  useEffect(() => {
    if (!open) setScheduleOpen(false);
  }, [open]);

  const handleDecline = () => {
    setScheduleOpen(false);
    onClose();
  };

  const handleConfirm = () => {
    setScheduleOpen(true);
  };

  return (
    <>
      <Dialog
        open={open && !scheduleOpen}
        onClose={handleDecline}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <CalendarIcon sx={{ color: '#3b82f6' }} />
          Agendar retorno?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#475569', lineHeight: 1.6 }}>
            O desfecho indica <strong>{summary}</strong>
            {patientName ? (
              <>
                {' '}
                para <strong>{patientName}</strong>
              </>
            ) : null}
            .
          </Typography>
          <Typography variant="body2" sx={{ color: '#475569', mt: 1.5, lineHeight: 1.6 }}>
            Deseja agendar o paciente em <strong>{formatOutcomeReturnDate(returnDate)}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleDecline} sx={{ textTransform: 'none', color: '#64748b' }}>
            Agora não
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            sx={{ bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, textTransform: 'none', fontWeight: 600 }}
          >
            Agendar
          </Button>
        </DialogActions>
      </Dialog>

      <AppointmentScheduleDialog
        open={scheduleOpen}
        onClose={handleDecline}
        stackAboveAppModals
        patientId={patientId}
        patientName={patientName}
        professionalId={professionalId}
        appointmentDate={returnDate}
        appointmentType={appointmentType}
        title="Retorno"
        onSaved={() => {
          onScheduled?.();
          setScheduleOpen(false);
          onClose();
        }}
      />
    </>
  );
};

export default EvolutionScheduleReturnPrompt;
